import { Configuration, EventType, PublicClientApplication, RedirectRequest } from '@azure/msal-browser';

// Environment-based configuration helper
const getEnvironmentConfig = () => {
  const { hostname } = window.location;

  // First check for build-time environment variable (set during Docker build)
  const buildTimeEnv = process.env.REACT_APP_ENVIRONMENT;

  // Determine environment based on build-time env var, then hostname fallback
  let environment: 'development' | 'test' | 'production' | 'demo' = 'development';

  if (buildTimeEnv) {
    // Use the environment set during build
    environment = buildTimeEnv as 'development' | 'test' | 'production' | 'demo';
    console.log(`Azure AD environment from REACT_APP_ENVIRONMENT: ${environment}`);
  } else {
    // Fallback to hostname detection (for local development)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      environment = 'development';
    } else if (hostname.includes('test') || hostname.includes('staging')) {
      environment = 'test';
    } else if (hostname.includes('prod')) {
      environment = 'production';
    }
    console.log(`Azure AD environment detected from hostname: ${environment} (hostname: ${hostname})`);
  }

  console.log(`Current origin: ${window.location.origin}`);

  console.log('Azure AD configuration values (public identifiers):');
  console.log(`REACT_APP_AZURE_CLIENT_ID: ${process.env.REACT_APP_AZURE_CLIENT_ID}`);
  console.log(`REACT_APP_AZURE_AUTHORITY: ${process.env.REACT_APP_AZURE_AUTHORITY}`);
  console.log(`REACT_APP_AZURE_TENANT_ID: ${process.env.REACT_APP_AZURE_TENANT_ID}`);
  console.log(`REACT_APP_AZURE_DOMAIN_NAME: ${process.env.REACT_APP_AZURE_DOMAIN_NAME}`);
  console.log(`REACT_APP_AZURE_POLICY: ${process.env.REACT_APP_AZURE_POLICY}`);

  // Switch statement for environment-specific configuration
  switch (environment) {
    case 'demo':
    case 'production': {
      console.log(`Using ${environment.toUpperCase()} Azure AD configuration`);
      const config = {
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
        authority: process.env.REACT_APP_AZURE_AUTHORITY || '',
        knownAuthorities: [process.env.REACT_APP_AZURE_DOMAIN_NAME || ''],
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        tenantId: process.env.REACT_APP_AZURE_TENANT_ID || '',
        domainName: process.env.REACT_APP_AZURE_DOMAIN_NAME || '',
        policy: process.env.REACT_APP_AZURE_POLICY || '',
        secureCookies: true,
        environment,
      };

      // Validate required credentials are present - fail fast if missing
      const requiredFields = [
        { name: 'REACT_APP_AZURE_CLIENT_ID', value: config.clientId },
        { name: 'REACT_APP_AZURE_AUTHORITY', value: config.authority },
        { name: 'REACT_APP_AZURE_TENANT_ID', value: config.tenantId },
        { name: 'REACT_APP_AZURE_DOMAIN_NAME', value: config.domainName },
        { name: 'REACT_APP_AZURE_POLICY', value: config.policy },
      ];

      const missingFields = requiredFields.filter((field) => !field.value);

      if (missingFields.length > 0) {
        const missing = missingFields.map((f) => f.name).join(', ');
        console.error(`Production Azure AD config missing: ${missing}`);
        throw new Error(`Production Azure AD credentials not configured. Missing: ${missing}`);
      }

      return config;
    }

    case 'test': {
      console.log('Using TEST Azure AD configuration');
      const config = {
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
        authority: process.env.REACT_APP_AZURE_AUTHORITY || '',
        knownAuthorities: [process.env.REACT_APP_AZURE_DOMAIN_NAME || ''],
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        tenantId: process.env.REACT_APP_AZURE_TENANT_ID || '',
        domainName: process.env.REACT_APP_AZURE_DOMAIN_NAME || '',
        policy: process.env.REACT_APP_AZURE_POLICY || '',
        secureCookies: true,
        environment,
      };

      // Validate required credentials are present - fail fast if missing
      const requiredFields = [
        { name: 'REACT_APP_AZURE_CLIENT_ID', value: config.clientId },
        { name: 'REACT_APP_AZURE_AUTHORITY', value: config.authority },
        { name: 'REACT_APP_AZURE_TENANT_ID', value: config.tenantId },
        { name: 'REACT_APP_AZURE_DOMAIN_NAME', value: config.domainName },
        { name: 'REACT_APP_AZURE_POLICY', value: config.policy },
      ];

      const missingFields = requiredFields.filter((field) => !field.value);

      if (missingFields.length > 0) {
        const missing = missingFields.map((f) => f.name).join(', ');
        console.error(`Test Azure AD config missing: ${missing}`);
        throw new Error(`Test Azure AD credentials not configured. Missing: ${missing}`);
      }

      return config;
    }

    case 'development':
    default:
      console.log('Using DEVELOPMENT Azure AD configuration');
      // Note: Local .env.development files shared via AWS Secrets Manager
      // Production deployments override these
      // via CircleCI contexts (stellar-production/stellar-test/stellar-development)
      return {
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
        authority: process.env.REACT_APP_AZURE_AUTHORITY || '',
        knownAuthorities: [process.env.REACT_APP_AZURE_DOMAIN_NAME || ''],
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        tenantId: process.env.REACT_APP_AZURE_TENANT_ID || '',
        domainName: process.env.REACT_APP_AZURE_DOMAIN_NAME || '',
        policy: process.env.REACT_APP_AZURE_POLICY || '',
        secureCookies: false,
        environment,
      };
  }
};

const envConfig = getEnvironmentConfig();

// Azure AD MSAL configuration with environment-based settings
export const msalConfig: Configuration = {
  auth: {
    clientId: envConfig.clientId,
    authority: envConfig.authority,
    redirectUri: envConfig.redirectUri,
    postLogoutRedirectUri: envConfig.postLogoutRedirectUri,
    knownAuthorities: envConfig.knownAuthorities,
  },
  cache: {
    cacheLocation: 'localStorage', // Using localStorage
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: number, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error(message);
            break;
          case 1: // LogLevel.Warning
            console.warn(message);
            break;
          case 2: // LogLevel.Info
            console.info(message);
            break;
          case 3: // LogLevel.Verbose
            console.debug(message);
            break;
          default:
            break;
        }
      },
      piiLoggingEnabled: false,
    },
  },
};

// Login request configuration
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// API request configuration - for Azure AD B2C, we need to include the client ID as scope for API access
export const apiRequest = {
  scopes: [envConfig.clientId], // Use client ID as scope for API access tokens
  forceRefresh: false,
};

// Logout request configuration
export const logoutRequest = {
  postLogoutRedirectUri: envConfig.postLogoutRedirectUri,
};

// Create and export the MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// MSAL initialization and redirect handling will be done in index.initialize.tsx

// Add event callback for login success )
msalInstance.addEventCallback((event) => {
  console.log('MSAL event:', event.eventType, event);
  if ((event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) && event.payload) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = event.payload as any;
    const { account } = payload;
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  }
});
